import { DEFAULT_LOCALE, type Locale } from '../i18n/resources';
import type { AppTheme } from '../types/theme';
import {
  DEFAULT_CHATBOT_CONVERSATIONS_PAGE_SIZE,
  DEFAULT_CHATBOT_TRAINING_EXAMPLES_PAGE_SIZE,
  DEFAULT_DOCUMENT_PAGE_SIZE,
  DEFAULT_GALLERY_PAGE_SIZE,
  PAGE_SIZES,
  USER_CONFIG_KEYS,
  type ChatbotConversationsPageSize,
  type ChatbotTrainingExamplesPageSize,
  type DocumentPageSize,
  type GalleryPageSize,
  type PageSize,
  type ResolvedUserConfig,
  type UserConfig,
  type UserConfigKey,
} from '../types/userConfig';
import {
  capPlatformChatLastSeenMessageIds,
  parsePlatformChatLastSeenMessageIds,
} from './platformChatLastSeenMessageIds';
import {
  clampRagMaxResults,
  clampRagMinScore,
  RAG_MAX_RESULTS_DEFAULT,
  RAG_MIN_SCORE_DEFAULT,
} from '../utils/ragRetrievalSettings';

export function getSystemTheme(): AppTheme {
  if (typeof window === 'undefined') {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function getSystemLocale(): Locale {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }

  const language = window.navigator.language.toLowerCase();
  return language.startsWith('de') ? 'de' : DEFAULT_LOCALE;
}

function isTheme(value: unknown): value is AppTheme {
  return value === 'light' || value === 'dark';
}

function isLocale(value: unknown): value is Locale {
  return value === 'en' || value === 'de';
}

function isPageSize(value: unknown): value is PageSize {
  return PAGE_SIZES.some((size) => size === value);
}

function isDocumentPageSize(value: unknown): value is DocumentPageSize {
  return isPageSize(value);
}

function isGalleryPageSize(value: unknown): value is GalleryPageSize {
  return isPageSize(value);
}

function isChatbotConversationsPageSize(
  value: unknown,
): value is ChatbotConversationsPageSize {
  return isPageSize(value);
}

function isChatbotTrainingExamplesPageSize(
  value: unknown,
): value is ChatbotTrainingExamplesPageSize {
  return isPageSize(value);
}

function parsePinnedKnowledgeDatabaseIds(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const ids = value.filter(
    (id): id is string => typeof id === 'string' && id.length > 0,
  );

  return [...new Set(ids)];
}

function parseBooleanConfig(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return undefined;
}

function parsePlatformChatMaxResults(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return clampRagMaxResults(value);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? clampRagMaxResults(parsed) : undefined;
  }
  return undefined;
}

function parsePlatformChatMinScore(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return clampRagMinScore(value);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? clampRagMinScore(parsed) : undefined;
  }
  return undefined;
}

export function parseUserConfigFields(raw: unknown): UserConfig {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }

  const stored = raw as Record<string, unknown>;
  const pinnedKnowledgeDatabaseIds = parsePinnedKnowledgeDatabaseIds(
    stored.pinnedKnowledgeDatabaseIds,
  );
  const replacePlatformChatDraftTextOnTranscription = parseBooleanConfig(
    stored.replacePlatformChatDraftTextOnTranscription,
  );
  const keepSidebarOpen = parseBooleanConfig(stored.keepSidebarOpen);
  const platformChatLastSeenMessageIds = parsePlatformChatLastSeenMessageIds(
    stored.platformChatLastSeenMessageIds,
  );
  const platformChatMaxResults = parsePlatformChatMaxResults(
    stored.platformChatMaxResults,
  );
  const platformChatMinScore = parsePlatformChatMinScore(
    stored.platformChatMinScore,
  );

  return {
    ...(isTheme(stored.theme) ? { theme: stored.theme } : {}),
    ...(isLocale(stored.locale) ? { locale: stored.locale } : {}),
    ...(isDocumentPageSize(stored.knowledgeDocumentsPageSize)
      ? { knowledgeDocumentsPageSize: stored.knowledgeDocumentsPageSize }
      : {}),
    ...(isGalleryPageSize(stored.galleryImagesPageSize)
      ? { galleryImagesPageSize: stored.galleryImagesPageSize }
      : {}),
    ...(isChatbotConversationsPageSize(stored.chatbotConversationsPageSize)
      ? {
          chatbotConversationsPageSize: stored.chatbotConversationsPageSize,
        }
      : {}),
    ...(isChatbotTrainingExamplesPageSize(
      stored.chatbotTrainingExamplesPageSize,
    )
      ? {
          chatbotTrainingExamplesPageSize:
            stored.chatbotTrainingExamplesPageSize,
        }
      : {}),
    ...(pinnedKnowledgeDatabaseIds !== undefined
      ? { pinnedKnowledgeDatabaseIds }
      : {}),
    ...(replacePlatformChatDraftTextOnTranscription !== undefined
      ? { replacePlatformChatDraftTextOnTranscription }
      : {}),
    ...(keepSidebarOpen !== undefined ? { keepSidebarOpen } : {}),
    ...(platformChatLastSeenMessageIds !== undefined
      ? {
          platformChatLastSeenMessageIds: capPlatformChatLastSeenMessageIds(
            platformChatLastSeenMessageIds,
          ),
        }
      : {}),
    ...(platformChatMaxResults !== undefined ? { platformChatMaxResults } : {}),
    ...(platformChatMinScore !== undefined ? { platformChatMinScore } : {}),
  };
}

export function parseUserConfigJson(value: string): UserConfig {
  try {
    return parseUserConfigFields(JSON.parse(value));
  } catch {
    return {};
  }
}

function parsePageSizeValue(value: string): PageSize | undefined {
  const parsed = Number(value);
  return isPageSize(parsed) ? parsed : undefined;
}

export function parseUserConfigEntry(key: string, value: string): UserConfig {
  switch (key) {
    case 'theme':
      return isTheme(value) ? { theme: value } : {};
    case 'locale':
      return isLocale(value) ? { locale: value } : {};
    case 'knowledgeDocumentsPageSize': {
      const pageSize = parsePageSizeValue(value);
      return pageSize !== undefined
        ? { knowledgeDocumentsPageSize: pageSize }
        : {};
    }
    case 'galleryImagesPageSize': {
      const pageSize = parsePageSizeValue(value);
      return pageSize !== undefined ? { galleryImagesPageSize: pageSize } : {};
    }
    case 'chatbotConversationsPageSize': {
      const pageSize = parsePageSizeValue(value);
      return pageSize !== undefined
        ? { chatbotConversationsPageSize: pageSize }
        : {};
    }
    case 'chatbotTrainingExamplesPageSize': {
      const pageSize = parsePageSizeValue(value);
      return pageSize !== undefined
        ? { chatbotTrainingExamplesPageSize: pageSize }
        : {};
    }
    case 'pinnedKnowledgeDatabaseIds': {
      try {
        const ids = parsePinnedKnowledgeDatabaseIds(JSON.parse(value));
        return ids !== undefined ? { pinnedKnowledgeDatabaseIds: ids } : {};
      } catch {
        return {};
      }
    }
    case 'replacePlatformChatDraftTextOnTranscription': {
      const parsed = parseBooleanConfig(value);
      return parsed !== undefined
        ? { replacePlatformChatDraftTextOnTranscription: parsed }
        : {};
    }
    case 'keepSidebarOpen': {
      const parsed = parseBooleanConfig(value);
      return parsed !== undefined ? { keepSidebarOpen: parsed } : {};
    }
    case 'platformChatLastSeenMessageIds': {
      try {
        const parsed = parsePlatformChatLastSeenMessageIds(JSON.parse(value));
        return parsed !== undefined
          ? {
              platformChatLastSeenMessageIds:
                capPlatformChatLastSeenMessageIds(parsed),
            }
          : {};
      } catch {
        return {};
      }
    }
    case 'platformChatMaxResults': {
      const parsed = parsePlatformChatMaxResults(value);
      return parsed !== undefined ? { platformChatMaxResults: parsed } : {};
    }
    case 'platformChatMinScore': {
      const parsed = parsePlatformChatMinScore(value);
      return parsed !== undefined ? { platformChatMinScore: parsed } : {};
    }
    default:
      return {};
  }
}

export function userConfigEntriesToPartial(
  entries: readonly { key: string; value: string }[],
): UserConfig {
  const raw: Record<string, unknown> = {};

  for (const entry of entries) {
    const partial = parseUserConfigEntry(entry.key, entry.value);
    for (const configKey of USER_CONFIG_KEYS) {
      if (partial[configKey] !== undefined) {
        raw[configKey] = partial[configKey];
      }
    }
  }

  return parseUserConfigFields(raw);
}

export function serializeUserConfigEntry(
  key: UserConfigKey,
  value: NonNullable<UserConfig[UserConfigKey]>,
): string {
  if (
    key === 'pinnedKnowledgeDatabaseIds' ||
    key === 'platformChatLastSeenMessageIds'
  ) {
    return JSON.stringify(value);
  }

  return String(value);
}

export function resolveUserConfig(
  partial: UserConfig = {},
): ResolvedUserConfig {
  return {
    theme: partial.theme ?? getSystemTheme(),
    locale: partial.locale ?? getSystemLocale(),
    knowledgeDocumentsPageSize:
      partial.knowledgeDocumentsPageSize ?? DEFAULT_DOCUMENT_PAGE_SIZE,
    galleryImagesPageSize:
      partial.galleryImagesPageSize ?? DEFAULT_GALLERY_PAGE_SIZE,
    chatbotConversationsPageSize:
      partial.chatbotConversationsPageSize ??
      DEFAULT_CHATBOT_CONVERSATIONS_PAGE_SIZE,
    chatbotTrainingExamplesPageSize:
      partial.chatbotTrainingExamplesPageSize ??
      DEFAULT_CHATBOT_TRAINING_EXAMPLES_PAGE_SIZE,
    pinnedKnowledgeDatabaseIds: partial.pinnedKnowledgeDatabaseIds ?? [],
    replacePlatformChatDraftTextOnTranscription:
      partial.replacePlatformChatDraftTextOnTranscription ?? false,
    keepSidebarOpen: partial.keepSidebarOpen ?? true,
    platformChatLastSeenMessageIds: capPlatformChatLastSeenMessageIds(
      partial.platformChatLastSeenMessageIds ?? {},
    ),
    platformChatMaxResults:
      partial.platformChatMaxResults ?? RAG_MAX_RESULTS_DEFAULT,
    platformChatMinScore: partial.platformChatMinScore ?? RAG_MIN_SCORE_DEFAULT,
  };
}

export function mergeUserConfig(
  current: ResolvedUserConfig,
  partial: UserConfig,
): ResolvedUserConfig {
  return {
    theme: partial.theme ?? current.theme,
    locale: partial.locale ?? current.locale,
    knowledgeDocumentsPageSize:
      partial.knowledgeDocumentsPageSize ?? current.knowledgeDocumentsPageSize,
    galleryImagesPageSize:
      partial.galleryImagesPageSize ?? current.galleryImagesPageSize,
    chatbotConversationsPageSize:
      partial.chatbotConversationsPageSize ??
      current.chatbotConversationsPageSize,
    chatbotTrainingExamplesPageSize:
      partial.chatbotTrainingExamplesPageSize ??
      current.chatbotTrainingExamplesPageSize,
    pinnedKnowledgeDatabaseIds:
      partial.pinnedKnowledgeDatabaseIds !== undefined
        ? partial.pinnedKnowledgeDatabaseIds
        : current.pinnedKnowledgeDatabaseIds,
    replacePlatformChatDraftTextOnTranscription:
      partial.replacePlatformChatDraftTextOnTranscription !== undefined
        ? partial.replacePlatformChatDraftTextOnTranscription
        : current.replacePlatformChatDraftTextOnTranscription,
    keepSidebarOpen:
      partial.keepSidebarOpen !== undefined
        ? partial.keepSidebarOpen
        : current.keepSidebarOpen,
    platformChatLastSeenMessageIds:
      partial.platformChatLastSeenMessageIds !== undefined
        ? capPlatformChatLastSeenMessageIds(
            partial.platformChatLastSeenMessageIds,
          )
        : current.platformChatLastSeenMessageIds,
    platformChatMaxResults:
      partial.platformChatMaxResults !== undefined
        ? clampRagMaxResults(partial.platformChatMaxResults)
        : current.platformChatMaxResults,
    platformChatMinScore:
      partial.platformChatMinScore !== undefined
        ? clampRagMinScore(partial.platformChatMinScore)
        : current.platformChatMinScore,
  };
}

export function applyUserConfigToDom(config: ResolvedUserConfig): void {
  document.documentElement.setAttribute('data-theme', config.theme);
  document.documentElement.setAttribute('lang', config.locale);
}
